// src/views/adminView/contractsView/detalle/TarifaMatrixReadOnly.jsx
// Vista de LECTURA del pricing real del contrato (motor de Nota de cobro):
// modo de impuesto, costo base, tarifas por zona, recargos por tamaño y reglas
// de ajuste. Ignora deliberadamente los arrays legacy (packageSizePricing,
// packageWeightPricing, collectPricing, shippingTypePricing). La edición vive
// en RateCardEditor.
import { useTranslation } from "react-i18next";
import { FileSpreadsheet } from "lucide-react";
import { Badge } from "../../../../components/ui/Badge";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { Table, THead, TBody, TR, TH, TD } from "../../../../components/ui/Table";
import { formatCLP } from "../../../../utils/formatters";

export default function TarifaMatrixReadOnly({ pricing, pricingVersion }) {
  const { t } = useTranslation();

  const zones = Array.isArray(pricing?.zonePricing) ? pricing.zonePricing : [];
  const sizes = Array.isArray(pricing?.sizeSurcharges) ? pricing.sizeSurcharges : [];
  const rules = Array.isArray(pricing?.rules) ? pricing.rules : [];
  const hasBase = pricing?.defaultBaseCost != null;
  const hasTax = pricing?.taxMode === "exclusive" || pricing?.taxMode === "inclusive";

  const hasAnything =
    hasBase || hasTax || zones.length > 0 || sizes.length > 0 || rules.length > 0;

  if (!hasAnything) {
    return (
      <EmptyState
        icon={<FileSpreadsheet aria-hidden="true" />}
        title={t("Sin tarifario configurado")}
        description={t("Configura la tarifa desde el editor.")}
      />
    );
  }

  const taxLabel = !hasTax
    ? "—"
    : pricing.taxMode === "exclusive"
      ? t("IVA excluido")
      : t("IVA incluido");

  return (
    <div className="space-y-5">
      {/* Parámetros generales */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-bg-muted rounded-lg p-3">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted">
            {t("Modo de impuesto")}
          </div>
          <div className="mt-1 text-sm font-medium text-fg">{taxLabel}</div>
        </div>
        <div className="bg-bg-muted rounded-lg p-3">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted">
            {t("Costo base por defecto")}
          </div>
          <div className="mt-1 text-sm font-medium text-fg tabular-nums">
            {hasBase ? formatCLP(pricing.defaultBaseCost) : "—"}
          </div>
        </div>
      </div>

      {/* Tarifas por zona */}
      <section>
        <h4 className="text-sm font-semibold text-fg mb-2">{t("Tarifas por zona")}</h4>
        {zones.length === 0 ? (
          <EmptyState className="py-6" title={t("Sin tarifas por zona")} />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t("Comunas")}</TH>
                <TH className="text-right">{t("Costo base")}</TH>
              </TR>
            </THead>
            <TBody>
              {zones.map((z, i) => {
                const communes = Array.isArray(z?.communeNames)
                  ? z.communeNames.join(", ")
                  : "—";
                return (
                  <TR key={i}>
                    <TD className="max-w-md">
                      <span className="block truncate" title={communes}>
                        {communes}
                      </span>
                    </TD>
                    <TD className="text-right font-semibold tabular-nums">
                      {z?.baseCost != null ? formatCLP(z.baseCost) : "—"}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </section>

      {/* Recargos por tamaño — se omite si no hay datos */}
      {sizes.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-fg mb-2">{t("Recargos por tamaño")}</h4>
          <Table>
            <THead>
              <TR>
                <TH>{t("Categoría")}</TH>
                <TH className="text-right">{t("Multiplicador")}</TH>
                <TH className="text-right">{t("Costo adicional")}</TH>
              </TR>
            </THead>
            <TBody>
              {sizes.map((s, i) => (
                <TR key={i}>
                  <TD className="font-medium">{s?.category || "—"}</TD>
                  <TD className="text-right tabular-nums">
                    {s?.multiplier != null ? `×${s.multiplier}` : "—"}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {s?.additionalCost != null ? formatCLP(s.additionalCost) : "—"}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </section>
      )}

      {/* Reglas de ajuste — se omite si no hay datos */}
      {rules.length > 0 && (
        <section>
          <h4 className="text-sm font-semibold text-fg mb-2">{t("Reglas de ajuste")}</h4>
          <Table>
            <THead>
              <TR>
                <TH>{t("Regla")}</TH>
                <TH>{t("Tipo")}</TH>
                <TH className="text-right">{t("Cálculo")}</TH>
                <TH className="text-right">{t("Prioridad")}</TH>
              </TR>
            </THead>
            <TBody>
              {rules.map((r, i) => (
                <TR key={r?.id ?? i}>
                  <TD className="font-medium">{r?.label || "—"}</TD>
                  <TD>
                    {r?.kind === "discount" ? (
                      <Badge tone="success">{t("Descuento")}</Badge>
                    ) : (
                      <Badge tone="warning">{t("Recargo")}</Badge>
                    )}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {r?.calc === "percent"
                      ? `${Math.round((Number(r?.value) || 0) * 100)}%`
                      : formatCLP(Number(r?.value) || 0)}
                  </TD>
                  <TD className="text-right tabular-nums">{r?.priority ?? 0}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </section>
      )}

      {/* Footer: versión vigente */}
      <div className="flex items-center gap-2 pt-1">
        <Badge tone="brand">{`v${pricingVersion ?? 1}`}</Badge>
        <span className="text-xs text-fg-muted">
          {t("Vista de lectura de la tarifa vigente · la edición se hace en el editor de tarifas.")}
        </span>
      </div>
    </div>
  );
}
